/**
 * Payment-schedule contract (CIRCULARITY_360 Phase C2) — shared by the deal-form editor and the
 * server's cash-calendar contributor so the two can never drift. A deal's payment terms are an
 * ordered list of TRANCHES; each is a share of the deal, anchored to the ORDER or the DELIVERY, at an
 * offset in days. Real cheques (with their own due dates) override; the schedule only estimates the
 * portion not yet backed by a cheque.
 */
import { toFaDigits } from './faDigits.js';
export const TRANCHE_ANCHORS = ['order', 'delivery'];
/**
 * How a tranche is collected — the heart of Iranian B2B terms, kept SEPARATE from timing (anchor +
 * offset already express advance-vs-balance and net-days): تی‌تی/حواله · چک · اعتبار اسنادی · نقد.
 * The method drives cash-flow coverage/confidence and the generated proforma sentence.
 */
export const TRANCHE_METHODS = ['tt', 'cheque', 'lc', 'cash'];
/** The default when a stored/legacy tranche carries no method — cheque is the norm for Iranian terms. */
export const DEFAULT_TRANCHE_METHOD = 'cheque';
/** Noun label for a method (dropdowns, headers). */
export const METHOD_LABEL_FA = {
    tt: 'حواله/تی‌تی',
    cheque: 'چک',
    lc: 'اعتبار اسنادی',
    cash: 'نقد',
};
/** In-sentence phrase for a method (proforma terms), e.g. «… با چک». */
const METHOD_PHRASE_FA = {
    tt: 'با حواله',
    cheque: 'با چک',
    lc: 'با اعتبار اسنادی',
    cash: 'نقدی',
};
/** Coerce one untrusted value into a valid tranche (clamped), or null if unusable. */
function coerceTranche(v) {
    if (!v || typeof v !== 'object')
        return null;
    const o = v;
    const pct = Number(o.pct);
    if (!Number.isFinite(pct) || pct < 0)
        return null;
    const anchor = o.anchor === 'delivery' ? 'delivery' : 'order';
    const offsetRaw = Number(o.offset_days);
    const offset_days = Number.isFinite(offsetRaw) ? Math.max(0, Math.round(offsetRaw)) : 0;
    const label = typeof o.label === 'string' ? o.label.slice(0, 60) : '';
    // Legacy tranches (stored before methods existed) have no method → default to cheque.
    const method = TRANCHE_METHODS.includes(o.method)
        ? o.method
        : DEFAULT_TRANCHE_METHOD;
    return { pct: Math.min(100, pct), anchor, offset_days, label, method };
}
/** Parse a JSON string or array into a validated tranche list (empty when unusable). */
export function parseTranches(input) {
    let arr = input;
    if (typeof input === 'string') {
        if (input.trim() === '')
            return [];
        try {
            arr = JSON.parse(input);
        }
        catch {
            return [];
        }
    }
    if (!Array.isArray(arr))
        return [];
    const list = arr.map(coerceTranche).filter((t) => t !== null);
    // Enforce the cross-tranche invariant coerceTranche can't see on its own: the cumulative share
    // never exceeds 100 (each tranche is clamped to the remaining headroom). A stored/API list summing
    // to >100 would otherwise over-project cash downstream (DOMAIN_AUDIT CA-3). Tranche COUNT is kept
    // so tagged-cheque indices stay aligned.
    let used = 0;
    for (const t of list) {
        t.pct = Math.min(t.pct, Math.max(0, 100 - used));
        used += t.pct;
    }
    return list;
}
/** Sum of tranche percentages (for UI validation — should be ~100). */
export function tranchesTotalPct(tranches) {
    return tranches.reduce((s, t) => s + t.pct, 0);
}
/** Persian timing phrase for a tranche's anchor + offset, e.g. «هنگام سفارش», «۶۰ روز پس از تحویل». */
function timingPhraseFa(t) {
    const base = t.anchor === 'delivery' ? 'تحویل' : 'سفارش';
    return t.offset_days > 0 ? `${toFaDigits(t.offset_days)} روز پس از ${base}` : `هنگام ${base}`;
}
/**
 * Render an ordered tranche list into the human-readable Persian payment term the proforma prints —
 * the SINGLE source of truth so the printed sentence and the cash projection can never disagree.
 * Each clause is «{٪} {label} {timing} {method}», e.g.
 * «۳۰٪ پیش‌پرداخت هنگام سفارش با حواله؛ ۷۰٪ تسویه پس از تحویل ۶۰ روز پس از تحویل با چک».
 * Returns '' for an empty schedule (the caller then falls back to the legacy free-text term).
 */
export function renderPaymentTerms(tranches) {
    const usable = tranches.filter((t) => t.pct > 0);
    if (!usable.length)
        return '';
    return usable
        .map((t) => {
        const parts = [
            `${toFaDigits(Math.round(t.pct))}٪`,
            t.label.trim(),
            timingPhraseFa(t),
            METHOD_PHRASE_FA[t.method],
        ].filter(Boolean);
        return parts.join(' ');
    })
        .join('؛ ');
}
//# sourceMappingURL=payments.js.map